import { LitElement, html, nothing } from 'lit';
import { TagKey } from '@cof/tag-catalog-fs-personal-mfe';
import { DebugService } from '../../../shared/services/debug.service.js';
import { style } from './step-employment-v2.style.js';
import { ApplicationService } from '../../../services/application.service.js';
import { REFI_PERSONAL_CONST } from '../../../shared/constants/app.constants.js';
import { globalStyle } from '../../../global-styles.style.js';
import './employment-active-toggle-v2/employment-active-toggle-v2.component.js';
import './employment-v2/employment-v2.component.js';
import './step-income-v2/step-iconic-income-v2.component.js';
import { validateForm } from '../../../shared/decorators/validate-form.decorator.js';
import { stratum } from '../../../../constants/stratum.js';

class RefiStepEmploymentV2Component extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiStepEmployment');
    this.application = {};
    this.validity = {};
    this.employmentMissing = false;
    this.cardOpen = false;
    this.isPrefill = false;
    this.financial = true;
    this.primaryChildIds = {
      employmentForm: 'employment-form',
      employmentActiveToggle: 'employment-active-toggle',
      income: 'income-input',
    };
    this.coAppChildIds = {
      coAppEmploymentForm: 'coapp-employment-form',
    };
    this.missingEmployment = false;
    this.pending = true;
  }

  static properties = {
    application: { type: Object, reflect: true },
    applicant: { type: Object, reflect: true },
    isPrefill: { type: Boolean, attribute: 'is-prefill' },
    isPrimary: { type: Boolean, attribute: 'is-primary', reflect: true },
    employmentStatus: { type: String, reflect: true },
    employedOrSelfEmployed: { type: Boolean, reflect: true },
  };

  static styles = [style, globalStyle];

  connectedCallback() {
    super.connectedCallback();
    this.initializeProperties();
    this.publishInitialPageViewTag();

    // Setting pending to false after a short delay to prevent double clicks from progressing the form further
    setTimeout(() => {
      this.pending = false;
    }, 200);
  }

  initializeProperties() {
    this.application = ApplicationService.Application;
    this.isJointApplication = this.application?.applicationType === 'JOINT';
    this.employedOrSelfEmployed = this.isEmployedOrSelfEmployed(
      this.application.primaryApplicant.employmentStatus
    );
    this.initialValues = {
      employmentStatus: this.application.primaryApplicant.employmentStatus,
      jobDescription:
        this.application.primaryApplicant.employmentInfo.jobDescription,
      activeIncomeVerification:
        this.application.primaryApplicant.employmentInfo
          .activeIncomeVerification,
      incomeAmount: this.application.primaryApplicant.incomeAmount,
      otherIncome: this.application.primaryApplicant.otherIncome,
    };
    this.initialCoAppValues = {
      employmentStatus: this.application.coApplicant.employmentStatus,
      jobDescription:
        this.application.coApplicant.employmentInfo.jobDescription,
      activeIncomeVerification:
        this.application.coApplicant.employmentInfo.activeIncomeVerification,
      incomeAmount: this.application.coApplicant.incomeAmount,
      otherIncome: this.application.coApplicant.otherIncome,
    };
    this.missingInfo =
      this.initialValues.employmentStatus === '' ||
      (this.employedOrSelfEmployed && this.initialValues.jobDescription === '');
  }

  publishInitialPageViewTag() {
    stratum.publishTag(
      TagKey.PAGE_VIEW_COAF_REFI_PREQUALIFY_PERSONAL_EMPLOYMENT,
      {
        replacements: { value: 'Employment Page' },
      }
    );
    if (this.application.profileType === REFI_PERSONAL_CONST.FINANCIAL) {
      stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_PREFILLED_FIELD, {
        replacements: {
          value: this.application.primaryApplicant.employmentStatus
            ? this.application.primaryApplicant.employmentStatus
            : 'null',
        },
      });
      stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_PREFILLED_FIELD, {
        replacements: {
          value: this.application.primaryApplicant.employmentInfo.jobDescription
            ? this.application.primaryApplicant.employmentInfo.jobDescription
            : 'null',
        },
      });
    }
  }

  renderHeading() {
    if (this.application.primaryApplicant?.employmentStatus?.length > 0) {
      return html`
        <refi-heading is-iconic>Tell us more about your job</refi-heading>
      `;
    }
    return html`
      <refi-heading is-iconic>Tell us about your job</refi-heading>
    `;
  }

  render() {
    return html`
      <form
        id="step-employment-v2-form"
        class="step-employment-v2-form"
        @submit="${this.onSubmit}"
        novalidate
      >
        ${this.renderHeading()} ${this.renderPrimaryEmploymentForm()}
        ${this.renderPrimaryEmploymentToggle()}
        ${this.renderPrimaryToggleError()} ${this.renderIncomeSection()}
        <!--        coApp-->
        ${this.renderCoApplicantEmploymentForm()} ${this.renderSubmitButton()}
        ${this.renderHelperText()}
      </form>
    `;
  }

  renderPrimaryEmploymentForm() {
    return html`
      <refi-personal-employment-v2
        id="${this.primaryChildIds.employmentForm}"
        ?compact="${this.isPrefill}"
        ?is-prefill="${this.isPrefill}"
        @validity="${this.updateValidity}"
        @primary-employment-changed="${this.editPrimaryEmploymentToggle}"
        @edit-card-closed="${this.editCardClosed}"
        @edit-card-open="${this.editCardOpened}"
        applicant-type="primaryApplicant"
      ></refi-personal-employment-v2>
    `;
  }

  renderPrimaryEmploymentToggle() {
    const showCard = this.shadowRoot.getElementById(
      this.primaryChildIds.employmentForm
    )?.displayEmploymentComponent;

    if (
      (showCard &&
        this.isEmployedOrSelfEmployed(
          this.application.primaryApplicant.employmentStatus
        )) ||
      (!showCard && this.employedOrSelfEmployed)
    ) {
      return html`
        <div class="employment-toggle">
          <refi-personal-employment-active-toggle-v2
            id="${this.primaryChildIds.employmentActiveToggle}"
            ?is-prefill="${this.isPrefill}"
            @validity="${this.updateValidity}"
            is-primary
          ></refi-personal-employment-active-toggle-v2>
        </div>
      `;
    }
    return html``;
  }

  renderPrimaryToggleError() {
    return this.missingEmployment
      ? html`
          <div class="missing-toggle">
            <fs-helper-text
              type="error"
              error="Please select an option above"
              id="employmentActiveInvalid"
            ></fs-helper-text>
          </div>
        `
      : nothing;
  }

  renderIncomeSection() {
    const className = this.missingInfo
      ? this.employedOrSelfEmployed
        ? 'income'
        : 'incomeWithoutEmploymentComponent'
      : 'income';
    return html`
      <div class="${className}">
        <refi-step-iconic-income-v2
          id="${this.primaryChildIds.income}"
          ?is-prefill="${this.application.isPrefilled}"
          is-primary
        ></refi-step-iconic-income-v2>
      </div>
    `;
  }

  renderCoApplicantEmploymentForm() {
    return this.isJointApplication
      ? html`
          <div class="coborrower-card">
            <div class="coborrower-card-content">
              <fs-heading size="normal" level="2" variant="primary">
                Co-borrower employment
              </fs-heading>
              ${this.renderCoApplicantEmploymentDetails()}
            </div>
          </div>
        `
      : nothing;
  }

  renderCoApplicantEmploymentDetails() {
    return html`
      <refi-personal-employment-v2
        id="${this.coAppChildIds.coAppEmploymentForm}"
        compact
        ?is-prefill="${this.isPrefill}"
        @validity="${this.updateValidity}"
        applicant-type="coApplicant"
      ></refi-personal-employment-v2>
    `;
  }

  renderSubmitButton() {
    return html`
      <fs-button class="fs-button next-button" variant="primary"
        >Next</fs-button
      >
    `;
  }

  renderHelperText() {
    return html`
      <fs-helper-text
        type="primary"
        helper="*Alimony, child support or separate maintenance income do not have to be included unless you want to use it to repay your loan"
        id="income-helper-text"
      ></fs-helper-text>
    `;
  }

  editCardOpened() {
    this.cardOpen = true;
  }

  editPrimaryEmploymentToggle(employedOrSelfEmployed) {
    this.employedOrSelfEmployed =
      employedOrSelfEmployed.detail.employedOrSelfEmployed;
    if (!this.employedOrSelfEmployed) {
      this.missingEmployment = false;
    }
    this.requestUpdate();
  }

  editCardClosed() {
    this.cardOpen = false;
  }

  updateValidity(ev) {
    this.validity[ev.detail.name] = ev.detail.isValid;

    if (ev.detail.name === 'activeIncomeVerification') {
      this.missingEmployment = false;
    }
    this.requestUpdate();
  }

  @validateForm()
  async onSubmit() {
    if (this.pending) {
      return;
    }

    this.debug.log('Validity', this.validity);

    const coAppCard = this.shadowRoot.getElementById(
      this.coAppChildIds.coAppEmploymentForm
    );
    if (coAppCard) {
      coAppCard.checkMissingEmployment();
    }

    // activeIncome not selected + employed
    const isPrimaryEmploymentInvalid =
      !this.validity.activeIncomeVerification && this.employedOrSelfEmployed;

    if (isPrimaryEmploymentInvalid || this.cardOpen) {
      if (isPrimaryEmploymentInvalid) {
        this.missingEmployment = true;
      }
      if (this.cardOpen) {
        this.shadowRoot
          .getElementById(this.primaryChildIds.employmentForm)
          .showEditError();
      }
      this.requestUpdate();
      return;
    }

    let valid = true;
    Object.values(this.validity).forEach((validityValue) => {
      if (!validityValue) {
        valid = false;
      }
    });
    if (valid) {
      this.saveApplicantInfo();

      stratum.publishTag(TagKey.PREQUALIFY_NEXT_PAGE_CTA, {
        replacements: { value: `employment` },
      });
      this.tagFieldChangesOnSubmit();
      this.dispatchEvent(new CustomEvent('next-step'));
    }
  }

  saveApplicantInfo() {
    this.shadowRoot
      .getElementById(this.primaryChildIds.employmentForm)
      .saveToApplication();
    if (this.employedOrSelfEmployed) {
      this.shadowRoot
        .getElementById(this.primaryChildIds.employmentActiveToggle)
        .saveToApplication();
    } else {
      this.application.primaryApplicant.employmentInfo.activeIncomeVerification =
        '';
    }
    this.shadowRoot
      .getElementById(this.primaryChildIds.income)
      .saveToApplication();

    if (this.isJointApplication) {
      this.shadowRoot
        .getElementById(this.coAppChildIds.coAppEmploymentForm)
        .saveToApplication();
    }
  }

  isEmployedOrSelfEmployed(employmentStatus) {
    return (
      employmentStatus === REFI_PERSONAL_CONST.EMPLOYMENT_STATUS.EMPLOYED ||
      employmentStatus === REFI_PERSONAL_CONST.EMPLOYMENT_STATUS.SELF_EMPLOYED
    );
  }

  tagFieldChangesOnSubmit() {
    // does not run in confirmation page
    const fieldsToCheck = [
      'employmentStatus',
      'jobDescription',
      'activeIncomeVerification',
      'incomeAmount',
      'otherIncome',
    ];

    if (this.employedOrSelfEmployed) {
      stratum.publishTag(TagKey.PERSONAL_EMPLOYMENT_PFCI_QUESTION, {
        replacements: {
          value:
            this.application.primaryApplicant.employmentInfo
              .activeIncomeVerification,
        },
      });
    }
    if (
      this.isJointApplication &&
      this.isEmployedOrSelfEmployed(
        this.application.coApplicant.employmentStatus
      )
    ) {
      stratum.publishTag(TagKey.PERSONAL_EMPLOYMENT_PFCI_QUESTION, {
        replacements: {
          value: `co-${this.application.coApplicant.employmentInfo.activeIncomeVerification}`,
        },
      });
    }

    fieldsToCheck.forEach((field) => {
      const initialValue = this.initialValues[field];
      const currentValue =
        this.application.primaryApplicant.employmentInfo[field] ||
        this.application.primaryApplicant[field];

      if (initialValue !== currentValue) {
        stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_CHANGED_FIELD, {
          replacements: {
            value: `${field}`,
          },
        });
      }

      if (this.isJointApplication) {
        const coAppInitialValue = this.initialCoAppValues[field];
        let coAppCurrentValue = this.application.coApplicant[field];
        if (
          coAppCurrentValue === undefined ||
          (coAppCurrentValue === null && field === 'otherIncome')
        ) {
          coAppCurrentValue =
            this.application.coApplicant.employmentInfo[field];
        }

        if (coAppCurrentValue !== coAppInitialValue) {
          stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_CHANGED_FIELD, {
            replacements: {
              value: `co-${field}`,
            },
          });
        }
      }
    });
  }
}

customElements.define('refi-step-employment-v2', RefiStepEmploymentV2Component);
